// Base selectors
import { createSelector } from 'reselect'

// Add this missing selector after the imports
export const selectAptitudeTopicsData = state =>
  state?.SubscribedInstitutes?.aptitudeTopics?.data ?? []
export const selectSubscribedInstitutes = state =>
  state?.SubscribedInstitutes?.subscribedInstitutes ?? []
export const selectSubscriptionRenewalInstitutes = state =>
  state?.SubscribedInstitutes?.subscriptionRenewalInstitutes ?? []
export const selectSelectedInstitute = state =>
  state?.SubscribedInstitutes?.selectedInstitute
export const selectLoadingStatus = state => state?.SubscribedInstitutes?.loading
export const selectError = state => state?.SubscribedInstitutes?.error
export const selectAptitudeTopics = state =>
  state?.SubscribedInstitutes?.aptitudeTopics?.data ?? []

export const selectAptitudeTopicsLoading = state =>
  state?.SubscribedInstitutes?.aptitudeTopics?.loading ?? false

export const selectAptitudeTopicsError = state =>
  state?.SubscribedInstitutes?.aptitudeTopics?.error ?? null

export const selectAptitudeConfig = state =>
  state?.SubscribedInstitutes?.aptitudeConfig?.data ?? {}

// Count selectors
export const selectTotalCollegeCount = state =>
  state?.SubscribedInstitutes?.totalCollegeCount
export const selectActiveAssessmentCount = state =>
  state?.SubscribedInstitutes?.activeAssessmentCount
export const selectAssessmentSentCount = state =>
  state?.SubscribedInstitutes?.assessmentSentCount ?? 0
export const selectAssessmentCompletedCount = state =>
  state?.SubscribedInstitutes?.assessmentCompletedCount ?? 0

// Metrics selectors
export const selectSubscribedInstitutesMetrics = state =>
  state?.SubscribedInstitutes?.subscribedInstitutesMetrics
export const selectSubscriptionRenewalMetrics = state =>
  state?.SubscribedInstitutes?.subscriptionRenewalMetrics

// Pagination selectors
export const selectSubscribedInstitutesPagination = state =>
  state?.SubscribedInstitutes?.subscribedInstitutesPagination
export const selectSubscriptionRenewalPagination = state =>
  state?.SubscribedInstitutes?.subscriptionRenewalPagination

// Filters selector
export const selectFilters = state => state?.SubscribedInstitutes?.filters

// Derived selectors
export const selectSubscribedInstitutesTotal = state =>
  selectSubscribedInstitutesMetrics(state)?.totalCount ?? 0
export const selectSubscriptionRenewalTotal = state =>
  selectSubscriptionRenewalMetrics(state)?.totalCount ?? 0

export const selectTrialInstitutes = state =>
  selectSubscribedInstitutes(state).filter(
    inst => inst.subscription_type === 'trial'
  )

export const selectSubscribedOnly = state =>
  selectSubscribedInstitutes(state).filter(
    inst => inst.subscription_type === 'subscribed'
  )

export const selectActiveAssessments = state =>
  state?.SubscribedInstitutes?.activeAssessments ?? []

export const selectActiveAssessmentsPagination = state =>
  state?.SubscribedInstitutes?.activeAssessmentsPagination ?? {
    page: 0,
    limit: 10,
    totalPages: 1,
    totalItems: 0,
    currentCount: 0,
  }

export const selectActiveAssessmentsMetrics = state =>
  state?.SubscribedInstitutes?.activeAssessmentsMetrics ?? {
    total: 0,
    completed: 0,
    pending: 0,
  }

export const selectActiveAssessmentById = (state, id) =>
  selectActiveAssessments(state).find(assessment => assessment.id === id) ||
  null

// Completed Assessments selectors
export const selectCompletedAssessments = state =>
  state?.SubscribedInstitutes?.completedAssessments ?? []

export const selectCompletedAssessmentsPagination = state =>
  state?.SubscribedInstitutes?.completedAssessmentsPagination ?? {
    page: 0,
    limit: 10,
    totalPages: 1,
    totalItems: 0,
    currentCount: 0,
  }

export const selectCompletedAssessmentsMetrics = state =>
  state?.SubscribedInstitutes?.completedAssessmentsMetrics ?? {
    total: 0,
    completedOnTime: 0,
    completedLate: 0,
  }

export const selectCompletedAssessmentById = (state, id) =>
  selectCompletedAssessments(state).find(assessment => assessment.id === id) ||
  null

export const selectInstitutesStates = state =>
  state?.SubscribedInstitutes?.institutesState ?? []

export const selectInstitutesCities = state =>
  state?.SubscribedInstitutes?.institutesCity ?? []

export const selectInstitutesNames = state =>
  state?.SubscribedInstitutes?.institutesName ?? []

export const selectSubscribedAssessment = state =>
  state?.SubscribedInstitutes?.subscribedAssessment ?? []

// Degree Search selectors
export const selectDegreeSearchResults = state =>
  state?.SubscribedInstitutes?.degreeSearch?.results ?? []

export const selectDegreeSearchLoading = state =>
  state?.SubscribedInstitutes?.degreeSearch?.loading ?? false

export const selectDegreeSearchError = state =>
  state?.SubscribedInstitutes?.degreeSearch?.error ?? null

// Derived degree search selectors
export const selectFormattedDegreeResults = state => {
  const results = selectDegreeSearchResults(state)
  return (
    results?.map(degree => ({
      ...degree,
      isSelected: false,
      streams:
        degree.streams?.map(stream => ({
          ...stream,
          isSelected: false,
          specializations:
            stream.specializations?.map(spec => ({
              ...spec,
              isSelected: false,
            })) ?? [],
        })) ?? [],
    })) ?? []
  )
}

export const selectAssessmentForms = state =>
  state?.SubscribedInstitutes?.assessmentForms?.forms ?? {}

export const selectActiveFormId = state =>
  state?.SubscribedInstitutes?.assessmentForms?.activeFormId

export const selectActiveForm = state => {
  const forms = selectAssessmentForms(state)
  const activeId = selectActiveFormId(state)
  return activeId ? forms[activeId] : null
}

export const selectRecentActivityLogs = state =>
  state?.SubscribedInstitutes?.recentActivityLog ?? []

// Add these selectors with the other selectors

// Assessment filters selectors
export const selectAssessmentFilters = state =>
  state?.SubscribedInstitutes?.assessmentFilters ?? {
    passingYear: '',
    Domain: '',
    degree: '',
    dept: '',
    specialization: '',
  }

// Institute data selectors
export const selectInstituteDomain = state =>
  state?.SubscribedInstitutes?.instituteDomain ?? []

export const selectInstituteDegree = state =>
  state?.SubscribedInstitutes?.instituteDegree ?? []
export const selectIsScheduling = state => state?.assessment?.isScheduling ?? false;

export const selectScheduleError = state => state?.assessment?.scheduleError ?? null;

export const selectScheduleSuccessMessage = state => state?.assessment?.scheduleSuccessMessage ?? '';

export const selectInstituteDepartment = state =>
  state?.SubscribedInstitutes?.instituteDepartment ?? []

export const selectInstituteSpecialization = state =>
  state?.SubscribedInstitutes?.instituteSpecialization ?? []

export const selectStudentDataSent = state =>
  state?.SubscribedInstitutes?.studentData?.sent ?? []

export const selectStudentDataPending = state =>
  state?.SubscribedInstitutes?.studentData?.pending ?? []

export const selectStudentDataInProgress = state =>
  state?.SubscribedInstitutes?.studentData?.inProgress ?? []

export const selectStudentDataDroppedOff = state =>
  state?.SubscribedInstitutes?.studentData?.droppedOff ?? []

export const selectStudentDataCompleted = state =>
  state?.SubscribedInstitutes?.studentData?.completed ?? []

export const selectStudentDataLoading = state =>
  state?.SubscribedInstitutes?.studentDataLoading ?? false

export const selectCompletedStudentsScores = state => {
  const completedStudents = selectStudentDataCompleted(state)
  return completedStudents.map(student => ({
    id: student.id,
    studentName: student.studentName,
    englishVerbalScore: student.englishVerbalScore,
    readingComprehensionScore: student.readingComprehensionScore,
    listeningAbilityScore: student.listeningAbilityScore,
  }))
}
// Utility selector to get student data by status type
export const selectStudentDataByStatus = (state, statusType) => {
  switch (statusType) {
    case 'sent':
      return selectStudentDataSent(state)
    case 'pending':
      return selectStudentDataPending(state)
    case 'inprogress':
      return selectStudentDataInProgress(state)
    case 'droppedoff':
      return selectStudentDataDroppedOff(state)
    case 'completed':
      return selectStudentDataCompleted(state)
    default:
      return []
  }
}
export const selectAssessmentCreationData = state =>
  state.SubscribedInstitutes.assessmentCreationData || []

export const selectAssessmentById = (state, id) => {
  const assessments = selectAssessmentCreationData(state)
  return assessments.find(assessment => assessment.id === id) || null
}

export const selectBulkUploadDataByAssessmentId = (state, id) => {
  const assessment = selectAssessmentById(state, id)
  return assessment?.bulkUploadData || []
}

export const selectSubscriptionStates = state =>
  state?.SubscribedInstitutes?.subscriptionStates || []

export const selectSubscriptionCities = state =>
  state?.SubscribedInstitutes?.subscriptionCities || []

export const selectSubscriptionInstitutes = state =>
  state?.SubscribedInstitutes?.subscriptionInstitutes || []

export const selectSubscriptionAssignLoading = state =>
  state?.SubscribedInstitutes?.subscriptionAssignLoading || false

export const selectSubscriptionAssignError = state =>
  state?.SubscribedInstitutes?.subscriptionAssignError

export const selectSubscriptionAssignSuccess = state =>
  state?.SubscribedInstitutes?.subscriptionAssignSuccess
export const selectAptitudeTopicsBySection = (state, sectionName) => {
  const allTopics = selectAptitudeTopics(state)
  const section = allTopics.find(section =>
    section.sectionName.toLowerCase() === sectionName.toLowerCase()
  )
  return section?.topics ?? []
}



export const selectRoleBasedAssessment = (state) => state.Assessment.roleBasedAssessment;
export const selectRoleBasedQuestions = state =>
  state?.SubscribedInstitutes?.roleBasedQuestions || { loading: false, data: null, error: null };

export const selectTopicBasedQuestions = state =>
  state?.SubscribedInstitutes?.hinglishQuestions || { loading: false, data: null, error: null };
// Backward compatibility alias
export const selectHinglishQuestions = selectTopicBasedQuestions;

// TPO Student List selectors
export const selectTpoStudentList = (state) =>
  state?.SubscribedInstitutes?.tpoStudentList || []

export const selectTpoStudentListPagination = (state) =>
  state?.SubscribedInstitutes?.tpoStudentListPagination || {
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
  }

export const selectTpoStudentListLoading = (state) =>
  state?.SubscribedInstitutes?.tpoStudentListLoading || false

export const selectTpoStudentListError = (state) =>
  state?.SubscribedInstitutes?.tpoStudentListError || null

// Chart filters selector
export const selectChartFilters = state =>
  state?.SubscribedInstitutes?.chartFilters ?? {
    cefrLevel: null,
    aptitudeLevel: null,
    consistency: null,
    assessmentType: null,
  }

// Chart data selectors
export const selectChartData = (state) =>
  state?.SubscribedInstitutes?.chartData || null

export const selectChartLoading = (state) =>
  state?.SubscribedInstitutes?.chartLoading || false

export const selectChartError = (state) =>
  state?.SubscribedInstitutes?.chartError || null

// Completed assessments metrics with infoCards (for KPI data from assessment states)
export const selectCompletedAssessmentsInfoCards = state =>
  state?.SubscribedInstitutes?.completedAssessmentsMetrics?.infoCards ?? {
    totalCandidates: 0,
    totalAssessmentsSent: 0,
    totalAssessmentsTaken: 0,
    totalPracticeTests: 0,
  }

// Specific assessment student list selectors
export const selectSpecificAssessmentStudentList = state => {
  const list = state?.SubscribedInstitutes?.specificAssessmentStudentList
  return list?.students || []
}

export const selectSpecificAssessmentStudentListPagination = createSelector(
  [state => state?.SubscribedInstitutes?.specificAssessmentStudentList?.totalCount],
  (totalCount) => ({
    total: totalCount || 0,
  })
)

export const selectSpecificAssessmentStudentListLoading = state =>
  state?.SubscribedInstitutes?.specificAssessmentStudentList?.loading || false

// Utility selector to get formatted topics for dropdowns
export const selectFormattedAptitudeTopics = createSelector(
  [selectAptitudeTopicsData],
  (aptitudeTopicsData) => {
    if (!aptitudeTopicsData || !Array.isArray(aptitudeTopicsData)) {
      return {}
    }

    const formatted = {}

    aptitudeTopicsData.forEach(section => {
      // Convert section name to the expected key format
      let sectionKey
      switch (section.sectionName.toLowerCase()) {
        case 'quantitative':
          sectionKey = 'quantitative'
          break
        case 'logical reasoning':
          sectionKey = 'logical_reasoning'
          break
        case 'critical reasoning':
          sectionKey = 'critical_reasoning'
          break
        default:
          sectionKey = section.sectionName.toLowerCase().replace(/\s+/g, '_')
      }

      // `selectable === false` means the topic sits outside the live question
      // blueprint (e.g. Analogy, Data Sufficiency once topic bands are seeded),
      // so it can never receive a question slot — offering it in the picker
      // would let an admin "choose" a topic that silently contributes nothing.
      // The flag is resolved per environment by the API; older responses omit
      // it, in which case every topic stays selectable.
      formatted[sectionKey] = section.topics
        .filter(topic => topic.selectable !== false)
        .map(topic => ({
          name: topic.Name,
          weight: topic.weight,
          subsectionId: topic.SubsectionId
        }))
    })

    return formatted
  }
)

// Export all selectors
const selectors = {
  selectSubscribedInstitutes,
  selectSubscriptionRenewalInstitutes,
  selectSelectedInstitute,
  selectLoadingStatus,
  selectError,
  selectTotalCollegeCount,
  selectActiveAssessmentCount,
  selectAssessmentSentCount,
  selectAssessmentCompletedCount,
  selectSubscribedInstitutesMetrics,
  selectSubscriptionRenewalMetrics,
  selectSubscribedInstitutesPagination,
  selectSubscriptionRenewalPagination,
  selectFilters,
  selectSubscribedInstitutesTotal,
  selectSubscriptionRenewalTotal,
  selectTrialInstitutes,
  selectSubscribedOnly,
  selectInstitutesStates,
  selectInstitutesCities,
  selectInstitutesNames,
  selectSubscribedAssessment,
  selectDegreeSearchResults,
  selectDegreeSearchLoading,
  selectDegreeSearchError,
  selectFormattedDegreeResults,
  selectActiveForm,
  selectActiveFormId,
  selectAssessmentForms,
  selectRecentActivityLogs,
  selectActiveAssessments,
  selectActiveAssessmentsPagination,
  selectActiveAssessmentsMetrics,
  selectActiveAssessmentById,
  selectCompletedAssessments,
  selectCompletedAssessmentsPagination,
  selectCompletedAssessmentsMetrics,
  selectCompletedAssessmentById,
  selectAssessmentFilters,
  selectInstituteDomain,
  selectInstituteDegree,
  selectInstituteDepartment,
  selectInstituteSpecialization,
  selectStudentDataSent,
  selectStudentDataPending,
  selectStudentDataInProgress,
  selectStudentDataDroppedOff,
  selectStudentDataCompleted,
  selectStudentDataLoading,
  selectStudentDataByStatus,
  selectAssessmentCreationData,
  selectAssessmentById,
  selectBulkUploadDataByAssessmentId,
  selectSubscriptionStates,
  selectSubscriptionCities,
  selectSubscriptionInstitutes,
  selectSubscriptionAssignLoading,
  selectSubscriptionAssignError,
  selectSubscriptionAssignSuccess,
  selectAptitudeTopics,
  selectAptitudeTopicsLoading,
  selectAptitudeTopicsError,
  selectAptitudeTopicsBySection,
  selectFormattedAptitudeTopics,
  selectRoleBasedQuestions,
  selectTpoStudentList,
  selectTpoStudentListPagination,
  selectTpoStudentListLoading,
  selectTpoStudentListError,
  selectChartFilters,
  selectChartData,
  selectChartLoading,
  selectChartError,
  selectCompletedAssessmentsInfoCards,
  selectSpecificAssessmentStudentList,
  selectSpecificAssessmentStudentListPagination,
  selectSpecificAssessmentStudentListLoading,
}

export default selectors
